import React, { useState, useRef, useEffect } from 'react';
import './VoiceRecorder.css';
import { getWebSpeechService, SpeechRecognitionResult, WordWithTimestamp } from '../utils/webSpeechAPI';
import { getSpeechStreamingService, StreamingRecognitionResult, PronunciationScore } from '../services/speechStreamingService';
import { EnhancedMediaRecorder } from '../utils/audioPreprocessing';
import LanguageSelector from './LanguageSelector';

interface VoiceRecorderProps {
  onRecordingComplete: (audioBlob: Blob, audioUrl: string, transcript?: SpeechRecognitionResult) => void;
  targetSentence?: string;
  onReset?: () => void;
  customPrompt?: string;
  hideLanguageSelector?: boolean;
  hideTranscription?: boolean;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ onRecordingComplete, targetSentence, onReset, customPrompt, hideLanguageSelector = false, hideTranscription = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [transcript, setTranscript] = useState<SpeechRecognitionResult | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [webSpeechAvailable, setWebSpeechAvailable] = useState(false);
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const [wordPronunciationScores, setWordPronunciationScores] = useState<Map<number, number>>(new Map());
  const [currentWordIndex, setCurrentWordIndex] = useState<number>(-1);
  const [useServerSideScoring, setUseServerSideScoring] = useState(true); // Default to server-side
  const [serverConnected, setServerConnected] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem('preferredAccent') || 'en-US'
  );
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const webSpeechRef = useRef(getWebSpeechService());
  const latestTranscriptRef = useRef<SpeechRecognitionResult | null>(null);
  const isTranscribingRef = useRef(false);
  const webSpeechAvailableRef = useRef(false);
  const audioWorkerRef = useRef<Worker | null>(null);
  const audioBufferRef = useRef<Float32Array | null>(null);
  const recordingStartTimeRef = useRef<number>(0);
  const speechStreamingRef = useRef(getSpeechStreamingService());
  const enhancedRecorderRef = useRef<EnhancedMediaRecorder | null>(null);
  const accumulatedWordsRef = useRef<Array<{
    word: string;
    startTime: number;
    endTime: number;
    confidence: number;
  }>>([]);
  const lastFinalResultIndexRef = useRef<number>(0);
  
  // Check Web Speech API availability on mount and initialize worker
  useEffect(() => {
    console.log('VoiceRecorder: Checking Web Speech API availability...');
    const available = webSpeechRef.current.isAvailable();
    console.log('VoiceRecorder: Web Speech API available:', available);
    setWebSpeechAvailable(available);
    webSpeechAvailableRef.current = available; // Keep ref in sync
    if (available) {
      console.log('Web Speech API is available for transcription');
      // Use persisted language or fall back to browser locale
      const persistedLang = localStorage.getItem('preferredAccent');
      const browserLang = navigator.language || 'en-US';
      const initialLang = persistedLang || browserLang;
      setSelectedLanguage(initialLang);
      webSpeechRef.current.setLanguage(initialLang);
      console.log(`Using accent: ${initialLang} (persisted: ${!!persistedLang})`);
    } else {
      console.log('Web Speech API not available - transcription disabled');
      console.log('Browser info:', navigator.userAgent);
      console.log('Protocol:', window.location.protocol);
      console.log('Hostname:', window.location.hostname);
    }
    
    // Initialize audio analysis worker
    audioWorkerRef.current = new Worker(new URL('../workers/audioAnalysisWorker.ts', import.meta.url));
    
    // Set up worker message handler
    audioWorkerRef.current.onmessage = (event) => {
      if (event.data.type === 'PRONUNCIATION_SCORE') {
        const { wordIndex, pronunciationScore } = event.data;
        if (wordIndex !== undefined && pronunciationScore !== undefined) {
          setWordPronunciationScores(prev => {
            const newScores = new Map(prev);
            newScores.set(wordIndex, pronunciationScore);
            return newScores;
          });
        }
      }
    };
    
    // Check server connection immediately and then periodically
    // Give the socket a moment to connect initially
    setTimeout(() => {
      setServerConnected(speechStreamingRef.current.isConnected());
    }, 500);
    
    const checkServerConnection = setInterval(() => {
      setServerConnected(speechStreamingRef.current.isConnected());
    }, 2000);
    
    // Cleanup on unmount
    return () => {
      if (audioWorkerRef.current) {
        audioWorkerRef.current.terminate();
      }
      clearInterval(checkServerConnection);
      // Don't disconnect the singleton streaming service - it should persist
      // speechStreamingRef.current.disconnect();
    };
  }, []);

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    localStorage.setItem('preferredAccent', language); // Persist selection
    if (webSpeechRef.current) {
      webSpeechRef.current.setLanguage(language);
      console.log(`Language changed to: ${language}`);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Set up audio context for visualization and processing
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      // Set up script processor for capturing audio data
      const scriptProcessor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      source.connect(scriptProcessor);
      scriptProcessor.connect(audioContextRef.current.destination);
      
      // Initialize audio buffer for continuous recording
      const bufferSize = audioContextRef.current.sampleRate * 60; // 60 seconds max
      audioBufferRef.current = new Float32Array(bufferSize);
      let bufferIndex = 0;
      recordingStartTimeRef.current = Date.now();
      
      scriptProcessor.onaudioprocess = (event) => {
        const inputData = event.inputBuffer.getChannelData(0);
        
        // Store audio for local processing
        if (audioBufferRef.current && bufferIndex + inputData.length < audioBufferRef.current.length) {
          audioBufferRef.current.set(inputData, bufferIndex);
          bufferIndex += inputData.length;
        }
        
        // Send audio to streaming service if using server-side scoring
        if (useServerSideScoring && speechStreamingRef.current.isCurrentlyStreaming()) {
          speechStreamingRef.current.sendAudioChunk(inputData, audioContextRef.current!.sampleRate);
        } else if (useServerSideScoring && !speechStreamingRef.current.isCurrentlyStreaming()) {
          console.log('[VoiceRecorder] Audio data available but streaming not active');
        }
      };
      
      // Start visualizing audio levels
      visualizeAudio();
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        console.log('MediaRecorder stopped');
        console.log('Current transcript ref value:', latestTranscriptRef.current);
        console.log('Is transcribing ref:', isTranscribingRef.current);
        console.log('Web Speech available ref:', webSpeechAvailableRef.current);
        
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Clean up audio visualization
        stream.getTracks().forEach(track => track.stop());
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        setAudioLevel(0);
        
        // If Web Speech API is active, wait for final transcript
        if (webSpeechAvailableRef.current && isTranscribingRef.current) {
          console.log('Waiting for final transcript...');
          console.log('Transcript ref at wait start:', latestTranscriptRef.current);
          // Store audio data temporarily
          const tempAudioBlob = audioBlob;
          const tempAudioUrl = audioUrl;
          let hasCalledComplete = false; // Flag to prevent duplicate calls
          
          // Set up a one-time listener for the final transcript
          let transcriptCheckCount = 0;
          const checkFinalTranscript = setInterval(() => {
            transcriptCheckCount++;
            const currentTranscript = latestTranscriptRef.current;
            console.log(`Checking for final transcript (attempt ${transcriptCheckCount}):`, currentTranscript?.transcript?.substring(0, 50), 'isFinal:', currentTranscript?.isFinal);
            
            // Check if we have a final transcript
            if (currentTranscript && currentTranscript.isFinal && !hasCalledComplete) {
              clearInterval(checkFinalTranscript);
              hasCalledComplete = true;
              setIsTranscribing(false);
              isTranscribingRef.current = false;
              console.log('Final transcript object:', currentTranscript);
              console.log('Final transcript text:', currentTranscript?.transcript);
              console.log('Final transcript isFinal:', currentTranscript?.isFinal);
              console.log('Passing to onRecordingComplete:', currentTranscript);
              onRecordingComplete(tempAudioBlob, tempAudioUrl, currentTranscript);
            }
          }, 100);
          
          // Stop Web Speech API (this will trigger the final transcript)
          webSpeechRef.current.stop();
          
          // Timeout fallback in case final transcript never arrives
          setTimeout(() => {
            clearInterval(checkFinalTranscript);
            if (!hasCalledComplete) {
              const currentTranscript = latestTranscriptRef.current;
              hasCalledComplete = true;
              console.log('Transcript timeout - using last known transcript:', currentTranscript);
              console.log('Transcript text at timeout:', currentTranscript?.transcript);
              onRecordingComplete(tempAudioBlob, tempAudioUrl, currentTranscript || undefined);
              setIsTranscribing(false);
              isTranscribingRef.current = false;
            }
          }, 2000);
        } else {
          // No transcription, proceed immediately
          onRecordingComplete(audioBlob, audioUrl, undefined);
        }
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setIsPaused(false);
      setWordPronunciationScores(new Map()); // Reset pronunciation scores
      setCurrentWordIndex(-1); // Reset current word index
      accumulatedWordsRef.current = []; // Reset accumulated words
      lastFinalResultIndexRef.current = 0; // Reset final result index
      
      // Start transcription and pronunciation scoring
      if (useServerSideScoring && serverConnected) {
        // Use server-side Google Speech-to-Text
        console.log('[VoiceRecorder] Starting server-side pronunciation scoring');
        console.log('[VoiceRecorder] Server connection status:', speechStreamingRef.current.isConnected());
        console.log('[VoiceRecorder] Setting isTranscribing to true');
        setIsTranscribing(true);
        isTranscribingRef.current = true;
        setTranscript(null);
        latestTranscriptRef.current = null;
        
        console.log('[VoiceRecorder] Calling startStreaming...');
        const streamStarted = await speechStreamingRef.current.startStreaming(
          (result: StreamingRecognitionResult) => {
            // Accumulate words across multiple results
            let allWords = [...accumulatedWordsRef.current];
            
            if (result.isFinal) {
              // For final results, add new words to accumulated list
              const newWords = result.words.slice(lastFinalResultIndexRef.current);
              allWords = [...accumulatedWordsRef.current, ...newWords];
              accumulatedWordsRef.current = allWords;
              lastFinalResultIndexRef.current = 0; // Reset for next utterance
            } else {
              // For interim results, show accumulated + current interim
              allWords = [...accumulatedWordsRef.current, ...result.words];
            }
            
            // Build full transcript from all words
            const fullTranscript = allWords.map(w => w.word).join(' ');
            
            // Convert to our format with all accumulated words
            const convertedResult: SpeechRecognitionResult = {
              transcript: fullTranscript,
              confidence: result.confidence,
              isFinal: result.isFinal,
              words: allWords.map(w => w.word),
              wordsWithTimestamps: allWords.map((w, idx) => ({
                word: w.word,
                startTime: w.startTime,
                endTime: w.endTime,
                confidence: w.confidence
              })),
              currentWordIndex: allWords.length - 1
            };
            
            setTranscript(convertedResult);
            latestTranscriptRef.current = convertedResult;
            
            // Update current word index based on timing
            const currentTime = Date.now() - recordingStartTimeRef.current;
            const currentIdx = allWords.findIndex(w => 
              currentTime >= w.startTime && currentTime <= w.endTime
            );
            if (currentIdx >= 0) {
              setCurrentWordIndex(currentIdx);
            }
          },
          (scores: PronunciationScore[]) => {
            // Update pronunciation scores from server
            // Adjust indices to account for accumulated words
            const baseIndex = accumulatedWordsRef.current.length;
            scores.forEach(score => {
              setWordPronunciationScores(prev => {
                const newScores = new Map(prev);
                // Map score to correct position in accumulated words
                const adjustedIndex = baseIndex + score.wordIndex;
                newScores.set(adjustedIndex, score.pronunciationScore);
                return newScores;
              });
            });
          },
          (error) => {
            console.error('[VoiceRecorder] Server streaming error:', error);
            console.log('[VoiceRecorder] Error callback triggered, setting isTranscribing to false');
            setIsTranscribing(false);
            isTranscribingRef.current = false;
          },
          audioContextRef.current?.sampleRate || 48000
        );
        
        console.log('[VoiceRecorder] startStreaming returned:', streamStarted);
        if (!streamStarted) {
          console.log('[VoiceRecorder] Failed to start server streaming, falling back to client-side');
          setUseServerSideScoring(false);
          setIsTranscribing(false);
          isTranscribingRef.current = false;
        } else {
          console.log('[VoiceRecorder] Server streaming started successfully');
        }
      } else if (webSpeechAvailable) {
        // Fall back to client-side Web Speech API
        setIsTranscribing(true);
        isTranscribingRef.current = true; // Keep ref in sync
        setTranscript(null);
        latestTranscriptRef.current = null; // Clear the ref as well
        
        const started = webSpeechRef.current.start(
          (result) => {
            setTranscript(result);
            latestTranscriptRef.current = result; // Keep ref updated with latest transcript
            
            // Update current word index and trigger pronunciation scoring
            if (result.currentWordIndex !== undefined) {
              setCurrentWordIndex(result.currentWordIndex);
              
              // Score pronunciation for completed words
              if (result.wordsWithTimestamps && audioBufferRef.current && audioContextRef.current) {
                result.wordsWithTimestamps.forEach((wordData, index) => {
                  // Only score words that haven't been scored yet
                  if (!wordPronunciationScores.has(index)) {
                    // Extract audio chunk for this word
                    const sampleRate = audioContextRef.current!.sampleRate;
                    const startSample = Math.floor((wordData.startTime / 1000) * sampleRate);
                    const endSample = Math.floor((wordData.endTime / 1000) * sampleRate);
                    
                    if (startSample >= 0 && endSample < audioBufferRef.current!.length) {
                      const audioChunk = audioBufferRef.current!.slice(startSample, endSample);
                      
                      // Send to worker for pronunciation scoring
                      if (audioWorkerRef.current) {
                        audioWorkerRef.current.postMessage({
                          type: 'PRONUNCIATION_SCORE',
                          audioData: audioChunk,
                          sampleRate: sampleRate,
                          word: wordData.word,
                          wordIndex: index
                        });
                      }
                    }
                  }
                });
              }
            }
            
            console.log('Transcript update:', result.transcript, 'Final:', result.isFinal);
            // If this is the final transcript, just log it (don't change isTranscribing here)
            if (result.isFinal) {
              console.log('Transcription completed with final result');
              // Note: We don't set isTranscribing(false) here to avoid race condition
              // The interval check in onstop will handle it
            }
          },
          (error) => {
            console.error('Web Speech API error:', error);
            setIsTranscribing(false);
            isTranscribingRef.current = false;
          }
        );
        
        if (!started) {
          setIsTranscribing(false);
          isTranscribingRef.current = false;
        }
      }
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Unable to access microphone. Please check your permissions.');
    }
  };
  
  const visualizeAudio = () => {
    if (!analyserRef.current) return;
    
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);
    
    // Calculate average volume
    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
    setAudioLevel(average / 255 * 100);
    
    animationFrameRef.current = requestAnimationFrame(visualizeAudio);
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      // Stop server-side streaming if active
      if (useServerSideScoring && speechStreamingRef.current.isCurrentlyStreaming()) {
        speechStreamingRef.current.stopStreaming();
      }
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setRecordingTime(0);
    }
  };
  
  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    }
  };
  
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const getPronunciationColor = (score: number): string => {
    if (score >= 0.8) return '#4CAF50'; // Green for good pronunciation
    if (score >= 0.5) return '#FFA726'; // Orange for medium
    return '#EF5350'; // Red for poor pronunciation
  };
  
  const renderTranscriptWithHighlighting = () => {
    if (!transcript || !transcript.wordsWithTimestamps) {
      return transcript?.transcript || 'Listening...';
    }
    
    return (
      <div className="transcript-words">
        {transcript.wordsWithTimestamps.map((wordData, index) => {
          const isCurrentWord = index === currentWordIndex;
          const pronunciationScore = wordPronunciationScores.get(index);
          
          let wordStyle: React.CSSProperties = {
            display: 'inline-block',
            margin: '0 4px',
            padding: '2px 6px',
            borderRadius: '3px',
            transition: 'all 0.3s ease',
          };
          
          if (isCurrentWord) {
            // Highlight current word being spoken
            wordStyle.backgroundColor = '#FFE082';
            wordStyle.fontWeight = 'bold';
            wordStyle.transform = 'scale(1.1)';
          } else if (pronunciationScore !== undefined) {
            // Color code based on pronunciation score
            wordStyle.color = getPronunciationColor(pronunciationScore);
            wordStyle.borderBottom = `2px solid ${getPronunciationColor(pronunciationScore)}`;
          }
          
          return (
            <span key={index} style={wordStyle}>
              {wordData.word}
            </span>
          );
        })}
      </div>
    );
  };
  
  // Debug mode from URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const debugMode = urlParams.get('debug') === 'true';
  
  return (
    <div className="voice-recorder">
      {/* Language selector for better recognition */}
      {webSpeechAvailable && !isRecording && !hideLanguageSelector && (
        <LanguageSelector
          currentLanguage={selectedLanguage}
          onLanguageChange={handleLanguageChange}
        />
      )}
      
      {/* Display target sentence if in guided mode */}
      {targetSentence && (
        <div className="target-sentence-display">
          <div className="target-sentence-label">
            {onReset && (
              <button className="change-sentence-btn" onClick={onReset}>
                ← Choose Different Sentence
              </button>
            )}
            <span>Read this sentence:</span>
          </div>
          <div className="target-sentence-text">
            {targetSentence}
          </div>
          <div className="target-sentence-tip">
            💡 Tip: Speak clearly and at a natural pace. The system will analyze your pronunciation.
          </div>
        </div>
      )}
      
      {/* Debug Panel */}
      {debugMode && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          background: '#f0f0f0',
          border: '2px solid #333',
          borderRadius: '5px',
          fontFamily: 'monospace',
          fontSize: '12px',
          zIndex: 1000,
          maxWidth: '400px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
        }}>
          <div style={{
            padding: '10px',
            background: '#333',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }} onClick={() => setDebugPanelOpen(!debugPanelOpen)}>
            <span>🔧 Debug: Transcript Status</span>
            <span>{debugPanelOpen ? '▼' : '▶'}</span>
          </div>
          {debugPanelOpen && (
            <div style={{padding: '10px'}}>
              <div>Web Speech Available: {webSpeechAvailable ? '✅ YES' : '❌ NO'}</div>
              <div>Is Transcribing: {isTranscribing ? '✅ YES' : '❌ NO'}</div>
              <div>Current Transcript: {transcript?.transcript?.substring(0, 100) || 'None'}</div>
              <div>Is Final: {transcript?.isFinal ? '✅ YES' : '❌ NO'}</div>
              <div>Word Count: {transcript?.words?.length || 0}</div>
              <div>Ref Value: {latestTranscriptRef.current?.transcript?.substring(0, 100) || 'None'}</div>
              <div>Ref Is Final: {latestTranscriptRef.current?.isFinal ? '✅ YES' : '❌ NO'}</div>
            </div>
          )}
        </div>
      )}
      
      <div className="recorder-display">
        <div className="time-display">{formatTime(recordingTime)}</div>
        <div className="audio-visualizer">
          <div 
            className="audio-level-bar" 
            style={{ 
              width: `${audioLevel}%`,
              backgroundColor: audioLevel > 70 ? '#ff4444' : audioLevel > 40 ? '#ffaa00' : '#4CAF50'
            }}
          />
        </div>
      </div>
      
      {/* Scoring Mode Toggle */}
      <div className="scoring-mode-toggle" style={{ 
        margin: '20px 0',
        padding: '15px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px'
      }}>
        <h4 style={{ margin: '0 0 10px 0' }}>Pronunciation Scoring Mode</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              name="scoringMode"
              checked={useServerSideScoring}
              onChange={() => setUseServerSideScoring(true)}
              disabled={isRecording}
              style={{ marginRight: '8px' }}
            />
            <div>
              <strong>Server-side (Google Cloud)</strong>
              <div style={{ fontSize: '12px', color: '#666' }}>
                More accurate • {serverConnected ? '✅ Connected' : '❌ Not connected'}
              </div>
            </div>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              name="scoringMode"
              checked={!useServerSideScoring}
              onChange={() => setUseServerSideScoring(false)}
              disabled={isRecording}
              style={{ marginRight: '8px' }}
            />
            <div>
              <strong>Client-side (Browser)</strong>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Works offline • {webSpeechAvailable ? '✅ Available' : '❌ Not available'}
              </div>
            </div>
          </label>
        </div>
      </div>
      
      {/* Live Transcript Display with Real-time Pronunciation Feedback */}
      {!hideTranscription && ((webSpeechAvailable || (useServerSideScoring && serverConnected)) && isTranscribing) && (
        <div className="live-transcript">
          <div className="transcript-header">
            <span className="transcript-badge">🔴 LIVE</span>
            <span className="transcript-label">
              Real-time Transcription with Pronunciation Feedback 
              {useServerSideScoring ? ' (Server)' : ' (Client)'}
            </span>
          </div>
          <div className="transcript-content">
            {transcript ? (
              <>
                <div className="transcript-text">{renderTranscriptWithHighlighting()}</div>
                {transcript.words.length > 0 && (
                  <div className="transcript-stats">
                    <span>Words: {transcript.words.length}</span>
                    <span>Confidence: {(transcript.confidence * 100).toFixed(0)}%</span>
                    {currentWordIndex >= 0 && (
                      <span>Current Word: {currentWordIndex + 1}/{transcript.wordsWithTimestamps?.length || 0}</span>
                    )}
                  </div>
                )}
                <div className="pronunciation-legend" style={{ marginTop: '10px', fontSize: '12px' }}>
                  <span style={{ color: '#4CAF50', marginRight: '10px' }}>● Good</span>
                  <span style={{ color: '#FFA726', marginRight: '10px' }}>● Medium</span>
                  <span style={{ color: '#EF5350' }}>● Needs Improvement</span>
                </div>
              </>
            ) : (
              <p className="transcript-waiting">Start speaking...</p>
            )}
          </div>
        </div>
      )}
      
      {/* Browser Compatibility Notice */}
      {!webSpeechAvailable && (
        <div className="compatibility-notice">
          <p>💡 For real-time transcription, use Chrome or Edge browser</p>
        </div>
      )}
      
      <div className="recorder-controls">
        {!isRecording ? (
          <button className="record-btn" onClick={startRecording}>
            <span className="record-icon">🎤</span>
            Start Recording
          </button>
        ) : (
          <>
            <button className="pause-btn" onClick={pauseRecording}>
              {isPaused ? '▶️ Resume' : '⏸️ Pause'}
            </button>
            <button className="stop-btn" onClick={stopRecording}>
              ⏹️ Stop
            </button>
          </>
        )}
      </div>
      
      {isRecording && (
        <div className="recording-indicator">
          <span className="recording-dot"></span>
          {isPaused ? 'Paused' : 'Recording...'}
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;